import { useEffect, useState } from "react";
import { Download, X, Share } from "lucide-react";

const IOS_KEY = "ass-hide-ios-install";
const DEFER_KEY = "ass-defer-install";

function isIOS() {
  const ua = window.navigator.userAgent || "";
  return /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
}

function isStandalone() {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

export default function InstallPWA() {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [showIOS, setShowIOS] = useState(false);

  useEffect(() => {
    if (isStandalone()) return;

    const onBIP = (e) => {
      e.preventDefault();
      setDeferred(e);
      const hidden = localStorage.getItem(DEFER_KEY);
      if (!hidden || Number(hidden) < Date.now()) setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", onBIP);

    // iOS: no beforeinstallprompt event, show a hint once
    if (isIOS() && !localStorage.getItem(IOS_KEY)) {
      const t = setTimeout(() => setShowIOS(true), 4000);
      return () => {
        clearTimeout(t);
        window.removeEventListener("beforeinstallprompt", onBIP);
      };
    }

    return () => window.removeEventListener("beforeinstallprompt", onBIP);
  }, []);

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    const { outcome } = await deferred.userChoice;
    if (outcome === "accepted" || outcome === "dismissed") {
      setDeferred(null);
      setVisible(false);
      if (outcome === "dismissed") {
        // Snooze for 7 days
        localStorage.setItem(DEFER_KEY, String(Date.now() + 7 * 24 * 60 * 60 * 1000));
      }
    }
  };

  const dismiss = () => {
    setVisible(false);
    localStorage.setItem(DEFER_KEY, String(Date.now() + 3 * 24 * 60 * 60 * 1000));
  };

  const dismissIOS = () => {
    setShowIOS(false);
    localStorage.setItem(IOS_KEY, "1");
  };

  if (showIOS) {
    return (
      <div
        data-testid="install-pwa-ios"
        className="fixed inset-x-3 bottom-3 z-50 rounded-2xl border border-[#E5B85C]/50 bg-[#111827] shadow-2xl p-4 flex gap-3 items-start"
      >
        <div className="shrink-0 w-10 h-10 rounded-lg bg-[#E5B85C]/20 flex items-center justify-center">
          <Share className="w-5 h-5 text-[#E5B85C]" />
        </div>
        <div className="flex-1 text-sm text-slate-200 leading-relaxed">
          <div className="font-display font-bold text-white mb-1">Installe ASS les Régionnaires</div>
          Ouvre le menu <b>Partager</b> puis choisis <b>“Sur l'écran d'accueil”</b> pour installer l'app.
        </div>
        <button
          data-testid="install-pwa-close-ios"
          onClick={dismissIOS}
          className="p-1.5 rounded text-slate-400 hover:text-white hover:bg-slate-800"
          aria-label="Fermer"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    );
  }

  if (!visible || !deferred) return null;

  return (
    <div
      data-testid="install-pwa"
      className="fixed inset-x-3 bottom-3 z-50 rounded-2xl border border-[#E5B85C]/50 bg-[#111827] shadow-2xl p-4 flex gap-3 items-center"
    >
      <div className="shrink-0 w-10 h-10 rounded-lg bg-[#E5B85C] flex items-center justify-center">
        <Download className="w-5 h-5 text-[#0B0F17]" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-display font-bold text-white text-sm">Installer l'app</div>
        <div className="text-xs text-slate-400 truncate">
          Accès rapide depuis ton écran d'accueil — hors-ligne partiel, plein écran.
        </div>
      </div>
      <button
        data-testid="install-pwa-cta"
        onClick={install}
        className="shrink-0 px-3 py-2 rounded-lg bg-[#E5B85C] hover:bg-[#D4A24E] text-[#0B0F17] text-xs font-bold uppercase tracking-wide"
      >
        Installer
      </button>
      <button
        data-testid="install-pwa-close"
        onClick={dismiss}
        className="shrink-0 p-1.5 rounded text-slate-400 hover:text-white hover:bg-slate-800"
        aria-label="Fermer"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
